import { useEffect, useRef, useState } from 'react';
import { useLoading } from 'react-simplikit';

import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Input } from '@/shared/components/shadcn/input';

type Props = {
  initialName: string;
  isOpen: boolean;
  onCancel: () => void;
  onSave: (campaignName: string) => Promise<void>;
  onUnmount: () => void;
};

export function RenameCampaignDialog({
  initialName,
  isOpen,
  onCancel,
  onSave,
  onUnmount,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(initialName);
  const [isLoading, startLoading] = useLoading();

  useEffect(() => {
    if (!isOpen) return;
    setValue(initialName);
  }, [initialName, isOpen]);

  const trimmedValue = value.trim();

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!isOpen && event.animationName === 'exit') {
      onUnmount();
    }
  };

  const handleSave = async () => {
    if (!trimmedValue || isLoading) return;

    try {
      await startLoading(onSave(trimmedValue));
    } catch {
      return;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onCancel() : undefined)}>
      <DialogContent
        ref={contentRef}
        className="sm:max-w-115"
        onAnimationEnd={handleAnimationEnd}
      >
        <DialogHeader>
          <DialogTitle>Rename campaign</DialogTitle>
          <DialogDescription>
            Update the campaign name shown across the list and dashboard.
          </DialogDescription>
        </DialogHeader>

        <Input
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="Enter campaign name"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={!trimmedValue} loading={isLoading}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
